import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ReactComponent as EditIcon } from '../../../../assets/common-assets/edit.svg';
import { lightPalette } from '../../../../theme';
import { classes, Root } from './utils';
import ChangePassword from '../../common-components/common-forms/change-password/ChangePassword';
import EditDetails from '../../common-components/common-forms/edit-details/EditDetails';
import { ACCOUNT_INFO } from '../../../../constants/staticContent';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import { changeStatusTfa } from '../../../../services/accountSetting';
import ROLES from '../../../../constants/roles';
import CustomTooltip from '../../utils/CustomTooltip';
import Toast from '../../utils/Toast';

const MyAccount = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editDetailOpen, setEditDetailOpen] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [tfaStatus, setTfaStatus] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const userDetail = useSelector((state) => state.user.user);
  const nameMerge = `${userDetail?.first_name} ${userDetail?.last_name}`;
  const accountName = userDetail ? nameMerge : 'Name';
  const userAvatar = userDetail ? userDetail?.avatar : '';
  const role = getLocalStorageItem('role');

  const handleAuthChecked = async (event) => {
    try {
      setTfaStatus(event.target.checked);
      const response = await changeStatusTfa(event.target.checked);
      if (response) {
        if (event.target.checked) {
          Toast({
            message: ACCOUNT_INFO.MY_ACCOUNT.TFA_STATUS.enabled,
            variant: 'success',
          });
        } else {
          Toast({
            message: ACCOUNT_INFO.MY_ACCOUNT.TFA_STATUS.disabled,
            variant: 'success',
          });
        }
      }
    } catch (error) {
      return error;
    }
  };

  const openChangePassword = () => {
    setOpen(true);
  };

  const openEditInfo = () => {
    setEditDetailOpen(true);
  };

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      setShowTooltip(windowWidth >= 600 && windowWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    if (userDetail && Object.keys(userDetail).length !== 0) {
      setIsLoading(false);
      setTfaStatus(userDetail?.is_tfa);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [userDetail]);

  let emailClassName = classes.emailBox;
  if (role === ROLES.MEMBER) {
    emailClassName = classes.emailBoxMember;
  }

  let tfaStatusText;
  if (tfaStatus) {
    tfaStatusText = 'Enabled';
  } else {
    tfaStatusText = 'Disabled';
  }

  return (
    <Root className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box>
            <Typography variant="h4" className={classes.myAccountTitle}>
              {ACCOUNT_INFO.MY_ACCOUNT.HEADING}
            </Typography>
          </Box>

          <Grid container className={classes.accountInfoBillingInfoSecondGrid}>
            <Grid item xs={5.78} className={classes.billingInfoSecondGridBoxes}>
              <Box className={classes.secondGridTitleBoarderBottom}>
                <Typography variant="h5" className={classes.gridItemMainTitle}>
                  {ACCOUNT_INFO?.BILLING_INFO?.BILLING_CONTACT}
                </Typography>
                <Box>
                  <EditIcon
                    className={classes.changePasswordButton}
                    onClick={openEditInfo}
                  />
                  {userDetail?.email && (
                    <Backdrop
                      className={classes.changePasswordBackdrop}
                      open={editDetailOpen}
                    >
                      <EditDetails
                        setEditDetailOpen={setEditDetailOpen}
                        isClosed={isClosed}
                        setIsClosed={setIsClosed}
                        editDetailOpen={editDetailOpen}
                        userDetail={userDetail}
                      />
                    </Backdrop>
                  )}
                </Box>
              </Box>
              <Box className={classes.addressAndPlanDetailBox}>
                <Box className={classes.userDetailBox}>
                  <Box>
                    <Avatar className={classes.avatar} src={userAvatar} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      color={lightPalette.darkPurple.main}
                      className={classes.userNameBox}
                    >
                      {accountName}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.AdminAddress}>
                  <Typography
                    variant="body1"
                    color={lightPalette.shoeWax.darker}
                  >
                    {userDetail?.email}
                  </Typography>
                  <Typography
                    variant="body1"
                    color={lightPalette.shoeWax.darker}
                  >
                    {userDetail?.phone_no}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={5.78} className={classes.gridBoxesContainer}>
              <Box
                className={`${classes.billingInfoFirstGridBoxes} ${classes.changePasswordGridBox}`}
              >
                <Box className={classes.GridTitleBoarderBottom}>
                  {showTooltip && (
                    <CustomTooltip
                      title={ACCOUNT_INFO?.BILLING_INFO?.CHANGE_PASSWORD}
                    >
                      <Typography
                        variant="h5"
                        className={classes.gridItemMainTitle}
                        noWrap
                      >
                        {ACCOUNT_INFO?.BILLING_INFO?.CHANGE_PASSWORD}
                      </Typography>
                    </CustomTooltip>
                  )}
                  {!showTooltip && (
                    <Typography
                      variant="h5"
                      className={classes.gridItemMainTitle}
                      noWrap
                    >
                      {ACCOUNT_INFO?.BILLING_INFO?.CHANGE_PASSWORD}
                    </Typography>
                  )}

                  <Box>
                    <EditIcon
                      className={classes.changePasswordButton}
                      onClick={openChangePassword}
                    />
                    <Backdrop
                      className={classes.changePasswordBackdrop}
                      open={open}
                    >
                      <ChangePassword setOpen={setOpen} />
                    </Backdrop>
                  </Box>
                </Box>
                <Box className={classes.subscriptionDetailBox}>
                  {showTooltip && (
                    <CustomTooltip title={userDetail?.email}>
                      <Typography
                        variant="body1"
                        color={lightPalette.shoeWax.darker}
                        className={emailClassName}
                      >
                        {userDetail?.email}
                      </Typography>
                    </CustomTooltip>
                  )}
                  {!showTooltip && (
                    <Typography
                      variant="body1"
                      color={lightPalette.shoeWax.darker}
                      className={emailClassName}
                    >
                      {userDetail?.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box className={classes.billingInfoFirstGridBoxes}>
                <Box className={classes.GridTitleBoarderBottom}>
                  {showTooltip && (
                    <CustomTooltip
                      title={ACCOUNT_INFO?.BILLING_INFO?.AUTH_TITLE}
                    >
                      <Typography
                        variant="h5"
                        className={classes.gridItemMainTitle}
                        noWrap
                      >
                        {ACCOUNT_INFO?.BILLING_INFO?.AUTH_TITLE}
                      </Typography>
                    </CustomTooltip>
                  )}
                  {!showTooltip && (
                    <Typography
                      variant="h5"
                      className={classes.gridItemMainTitle}
                      noWrap
                    >
                      {ACCOUNT_INFO?.BILLING_INFO?.AUTH_TITLE}
                    </Typography>
                  )}
                </Box>
                <Box className={classes.subscriptionDetailBox} gap={2}>
                  <Typography
                    variant="body1"
                    color={lightPalette.shoeWax.darker}
                  >
                    {tfaStatusText}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={tfaStatus}
                        onChange={handleAuthChecked}
                      />
                    }
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Root>
  );
};

export default MyAccount;
